import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonItem,
  IonLabel,
  IonNote,
  IonProgressBar,
  IonText,
} from "@ionic/react";
import { getFunctions, httpsCallable } from "firebase/functions";
import React, { useState } from "react";

interface KindReport {
  scanned: number;
  public: number;
  restricted: number;
  skipped: number;
  staleRemoved: { public: number; restricted: number };
}

interface ReindexResult {
  indices: {
    songs: { public: string; restricted: string };
    artists: { public: string; restricted: string };
  };
  restrictedIndexing: boolean;
  songs: KindReport;
  artists: KindReport;
}

/**
 * Admin "Reindex Search" tab. The rebuild runs server-side in the
 * reindexSearch Cloud Function (admin / super admin only) — the browser no
 * longer holds any Algolia write access (security finding S03).
 */
const ReindexSearch: React.FC = () => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ReindexResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (running) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const reindex = httpsCallable<void, ReindexResult>(getFunctions(), "reindexSearch", {
        timeout: 540000,
      });
      const { data } = await reindex();
      setResult(data);
    } catch (err: any) {
      console.error("[ReindexSearch] error", err);
      setError(err?.message || "Reindex failed");
    } finally {
      setRunning(false);
    }
  };

  const renderKind = (label: string, r: KindReport, names: { public: string; restricted: string }) => (
    <IonCard style={{ marginTop: 12 }}>
      <IonCardHeader color="success">
        <IonCardTitle>{label}</IonCardTitle>
        <IonCardSubtitle>
          {names.public} / {names.restricted}
        </IonCardSubtitle>
      </IonCardHeader>
      <IonCardContent>
        <IonText>
          <p><strong>Scanned:</strong> {r.scanned}</p>
          <p><strong>Public records:</strong> {r.public}</p>
          <p><strong>Restricted records:</strong> {r.restricted}</p>
          <p><strong>Skipped (not indexable):</strong> {r.skipped}</p>
          <p>
            <strong>Stale entries removed:</strong> {r.staleRemoved.public} public,{" "}
            {r.staleRemoved.restricted} restricted
          </p>
        </IonText>
      </IonCardContent>
    </IonCard>
  );

  return (
    <IonCard>
      <IonCardHeader color="primary">
        <IonCardTitle>Reindex Search</IonCardTitle>
        <IonCardSubtitle>
          Rebuild the search indexes from Firestore and remove entries that no
          longer match each song's or profile's privacy.
        </IonCardSubtitle>
      </IonCardHeader>

      <IonCardContent>
        <IonItem lines="none">
          <IonLabel className="ion-text-wrap">
            <IonNote color="medium">
              Runs on the server (up to 9 minutes). Edits are kept in sync
              automatically; use this after first deploying search sync, after
              changing privacy in bulk, or if results look out of date.
            </IonNote>
          </IonLabel>
        </IonItem>

        <IonButtons style={{ marginTop: 12, gap: 8, flexWrap: "wrap" }}>
          <IonButton fill="solid" color="favorite" disabled={running} onClick={run}>
            Reindex Artists and Songs
          </IonButton>
        </IonButtons>

        {running && <IonProgressBar type="indeterminate" />}
        {error && (
          <IonText color="danger">
            <p role="alert">Error: {error}</p>
          </IonText>
        )}
        {result && !result.restrictedIndexing && (
          <IonText>
            <p>Restricted indexing is off for this instance: only public material is searchable.</p>
          </IonText>
        )}
        {result && renderKind("Artists", result.artists, result.indices.artists)}
        {result && renderKind("Songs", result.songs, result.indices.songs)}
      </IonCardContent>
    </IonCard>
  );
};

export default ReindexSearch;
